<template>
  <div :class="userData.id > 0 ? 'logged-in' : 'anonymous-user'">
    <div class="header-logo mt-3 mb-2 mx-auto d-flex justify-content-center">
      <Logo />
    </div>
    <div class="submission-wrapper">
      <EntitySingle
        :config="config"
        :entity.sync="entity"
        :extra-validation="extraValidation"
        :extra-actions="extraActions"
        :edit-mode="editMode"
        save-label="Save draft"
        actions-always-visible
        @entityFetched="updateEntity"
        @entitySaved="setEntity"
      >
        <b-tabs
          v-if="ungrouppedSections.length"
          :key="key"
          content-class="col-12 col-md-9 mt-1 mt-md-0"
          nav-wrapper-class="col-md-3 col-12"
          nav-class="nav-left"
          vertical
          pills
        >
          <b-tab
            v-for="section in ungrouppedSections"
            :key="section.id"
            :disabled="fieldDisabled(section)"
          >
            <template #title>
              <span class="font-weight-bold">{{ section.title }}</span>
            </template>
            <EntityEditSection
              :module="config.module"
              :entity.sync="entity"
              :form-fields="getSectionFields(section.id)"
              allow-autofocus
            />
          </b-tab>
        </b-tabs>
        <div
          v-if="sectionsGroups.length"
          :key="key"
        >
          <div
            v-for="group in sectionsGroups"
            :key="group.id"
          >
            <h2 class="mt-2 mb-1">
              {{ group.title }}
            </h2>
            <b-tabs
              content-class="col-12 col-md-9 mt-1 mt-md-0"
              nav-wrapper-class="col-md-3 col-12"
              nav-class="nav-left"
              vertical
              pills
            >
              <b-tab
                v-for="section in groupSections(group.id)"
                :key="section.id"
                :disabled="fieldDisabled(section)"
              >
                <template #title>
                  <span class="font-weight-bold">{{ section.title }}</span>
                </template>
                <EntityEditSection
                  :module="config.module"
                  :entity.sync="entity"
                  :form-fields="getSectionFields(section.id)"
                  allow-autofocus
                />
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </EntitySingle>
    </div>
  </div>
</template>

<script>
import EntityEditSection from '@/layouts/entity/EntityEditSection.vue'
import EntitySingle from '@/layouts/entity/EntitySingle.vue'
import { getUserRights } from '@/auth/utils'
import { translationUtils } from '@core/mixins/ui/translations'
import Logo from '@core/layouts/components/Logo.vue'
import storeModule from './storeModule'

export default {
  components: {
    EntityEditSection,
    EntitySingle,
    Logo
  },
  mixins: [translationUtils],
  data() {
    return {
      entity: {
        requestType: { id: 0 },
        fields: [],
        colSize: 3,
      },
      allFields: [],
      membersOnly: false,
      key: 1,
    }
  },
  computed: {
    userData() {
      return this.$store.getters['app-common/userData']
    },
    requestTypeId() {
      return this.$route.params.rid
    },
    isManager() {
      return getUserRights().includes('Request_Type_Management_Request_Type_Edit')
    },
    editMode() {
      return !this.entity.id || !this.entity.published || (!this.entity.finalized && this.isManager)
    },
    config() {
      return {
        store: storeModule,
        module: 'submissions',
        endpoint: 'submissions',
        route: 'request-submissions',
        title: {
          single: this.$t('Submission Add'),
          plural: this.$t('My Submissions'),
        },
      }
    },
    extraActions() {
      if (this.entity.id) {
        return [
          {
            callback: this.printAnswer,
            icon: 'PrinterIcon',
            title: 'Print',
            variant: 'secondary',
            renderCondition: {
              key: 'published',
              value: true,
            },
          },
          {
            endpoint: 'publish',
            icon: 'SendIcon',
            title: 'Submit',
            validate: true,
            renderCondition: {
              key: 'published',
              value: false,
            },
            propertyNotExists: 'finalized',
          },
          {
            endpoint: 'finalize',
            icon: 'CheckCircleIcon',
            title: 'Accept',
            variant: 'success',
            validate: true,
            renderCondition: {
              key: 'published',
              value: true,
            },
            authorityValidation: this.isManager
          },
        ]
      }
      return []
    },
    selectMultipleIds() {
      return (
        this.allFields
          .filter(field => field.type === 'SELECTMULTIPLE')
          .map(field => field.id) || []
      )
    },
    fields() {
      return [
        {
          id: 'comments',
          type: 'textarea',
          title: this.$t('comments'),
          size: 12,
        },
      ].map(field => ({ ...field, section: { title: this.$t('comments') } }))
    },
    sections() {
      return [
        ...new Set(this.allFields.map(item => JSON.stringify(item.section))),
      ].map(i => JSON.parse(i))
    },
    ungrouppedSections() {
      return this.sections.filter(item => item.group == null)
    },
    sectionsGroups() {
      return [
        ...new Set(this.sections.filter(item => item.group != null).map(item => JSON.stringify(item.group))),
      ].map(i => JSON.parse(i))
    }
  },
  async mounted() {
    if (this.requestTypeId) {
      this.entity.requestType.id = this.requestTypeId
      this.fetchFields(this.requestTypeId)
    }
  },
  methods: {
    async fetchFields(rId) {
      const response = await this.$store.dispatch('app-common/fetchRequestTypeFields', rId)
      this.membersOnly = response.data.membersOnly
      if (this.membersOnly && !this.userData.id) {
        this.$router.replace({ name: 'login' })
      }
      this.allFields = [...response.data.fields, ...this.fields]
    },
    fieldDisabled(field) {
      if (field.dependentField) {
        const val = field.dependentFieldValue
        const entityVal = this.entity[field.dependentField.id]
        const isMultiple = Array.isArray(entityVal)
        if (val.includes('!')) {
          if (isMultiple) {
            return !(entityVal && !entityVal.includes(val.replace('!', '')))
          }
          return entityVal == val.replace('!', '')
        } else if (val.includes(',')) {
          const arr = val.split(',')
          if (isMultiple) {
            return !(entityVal && entityVal.some(r => arr.includes(r)))
          }
          return !arr.includes(entityVal)
        }
        if (isMultiple) {
          return !(entityVal && entityVal.includes(val))
        }
        return entityVal != val
      }
      return false
    },
    getSectionFields(sectionId) {
      return this.allFields
        .filter(item => item.section.id === sectionId)
        .map(field => {
          let type = field.type.toLowerCase()
          let taggable = false
          let disabled = false
          let readonly = !this.editMode
          let autocompleteUrl = ''
          if (field.type == 'VIRTUAL') {
            type = 'text'
            readonly = true
            disabled = true
          } else if (field.hideable && this.entity.id) {
            type = 'password'
          }
          return {
            id: field.id,
            type,
            object: field.object,
            options: field.options || [],
            label: field.title,
            suffix: field.unit ? field.unit.title : null,
            extraClass: field.required ? 'required-field' : null,
            readonly,
            taggable,
            autocompleteUrl,
            pushTags: taggable,
            disabled,
            colSize: field.size,
            helpText: field.helpText,
            hideToggle: !this.isManager,
            hidden: this.fieldDisabled(field),
            step: type == 'number' ? 'any' : null,
            optionsCnt: 1,
            rules:
              (field.minValue ? 'min_value:' + field.minValue : '') +
              (field.minValue && field.maxValue ? '|' : '') +
              (field.maxValue ? 'max_value:' + field.maxValue : ''),
            autoupload: true,
          }
        })
    },
    async updateEntity(entity) {
      if (!this.requestTypeId) {
        await this.fetchFields(this.entity.requestType.id)
        if (entity) {
          this.setEntity(entity)
        }
        this.key = 2
      }
    },
    setEntity(entity) {
      entity.fields.forEach(item => {
        const val = this.selectMultipleIds.includes(parseInt(item.field.id))
          ? item.value.split(',')
          : item.value
        this.entity[item.field.id] = val
      })
      this.entity.fields = []
    },
    extraValidation(action) {
      this.entity.fields = []
      Object.keys(this.entity).forEach(key => {
        if (!isNaN(key)) {
          const val = this.selectMultipleIds.includes(parseInt(key))
            ? this.entity[key].join()
            : this.entity[key]

          this.entity.fields.push({
            field: { id: key },
            value: val,
          })
          // delete this.entity[key]
        }
      })
      if (action?.endpoint == 'publish' && !this.entity.fields.length) {
        return this.$t('MISSING_EXTRA_RECORDING_FIELDS')
      }
      return ''
    },
    printAnswer() {
      this.$router.push({ name: 'request-submissions-print', params: { rid: this.entity.requestType.id, id: this.entity.id } })
    },
    groupSections(groupId) {
      return this.sections.filter(item => item.group != null && item.group.id == groupId)
    },
  },
}
</script>
<style lang="scss">
.anonymous-user {
  .breadcrumb {
    display: none;
  }
}

.header-logo {
  max-width: 280px;
}

.submission-wrapper {
  width: 90%;
  margin: 0 auto;
  padding: 32px 20px 10px;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 1px 1px 12px -8px #000000;
}
</style>